import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Container,
  Header,
  Title,
  SearchInput,
  SearchResults,
  SearchResult,
  ResultName,
  ResultDetail as ResultLocation,
  SearchMessage,
} from '../../shared/component/modal/SearchModalLayout';
import deleteIcon from '../../asset/icon/delete.png';
import { searchVenue } from '../../shared/api/venueApi';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { getHintMessage } from '../../shared/constants/hintMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { getModalMessage } from '../../shared/constants/modalMessages';
import type { SimpleVenueData } from '../../shared/dto/EntityRepository';
import { ModalType } from '../../shared/enum/EnumRepository';
import { useModalStore } from '../../shared/store/portal/modalStore';

const ProjectInfoVenueSearchModal: React.FC = () => {
  const { standardModal, isClosing, setStandardModal, clearModal } = useModalStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SimpleVenueData[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [completedQuery, setCompletedQuery] = useState('');
  const latestSearchId = useRef(0);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clearModal]);

  useEffect(() => {
    const normalizedQuery = query.trim();
    const searchId = ++latestSearchId.current;

    if (!normalizedQuery || isClosing) {
      setResults([]);
      setIsSearching(false);
      setSearchFailed(false);
      setCompletedQuery('');
      return;
    }

    setIsSearching(true);
    setSearchFailed(false);
    setCompletedQuery('');
    const timer = window.setTimeout(async () => {
      try {
        const response = await searchVenue(normalizedQuery);
        if (latestSearchId.current !== searchId) return;
        setResults(response.data.venues ?? []);
        setCompletedQuery(normalizedQuery);
      } catch {
        if (latestSearchId.current !== searchId) return;
        setResults([]);
        setSearchFailed(true);
      } finally {
        if (latestSearchId.current === searchId) setIsSearching(false);
      }
    }, 200);

    return () => {
      window.clearTimeout(timer);
      if (latestSearchId.current === searchId) latestSearchId.current += 1;
    };
  }, [query, isClosing]);

  const handleSelect = (venue: SimpleVenueData) => {
    standardModal?.handleChange(String(venue.id), venue);
    clearModal();
  };

  const handleVenueRequest = () => {
    setStandardModal({
      modalType: ModalType.VENUE_REQUEST,
      title: null,
      value: query.trim(),
      handleChange: () => {},
    });
  };

  const hasNoResults = !isSearching
    && !searchFailed
    && completedQuery === query.trim()
    && Boolean(completedQuery)
    && !isClosing
    && results.length === 0;

  return (
    <Container>
      <Header>
        <Title>{getHintMessage('CONNECT_VENUE')}</Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          hoverOpacity
          backgroundImg={deleteIcon}
          onClick={() => clearModal()}
          aria-label="Close"
        />
      </Header>
      <SearchInput
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search Venues"
        aria-label="Search Venues"
        autoFocus
      />
      <SearchResults>
        {isSearching && <SearchMessage>Searching…</SearchMessage>}
        {!isSearching && searchFailed && <SearchMessage>Search failed.</SearchMessage>}
        {hasNoResults && (
          <EmptyState>
            <EmptyAction>
              <span>{getModalMessage('VENUE_SEARCH_NO_RESULTS')}</span>
              <HeadlessBtn
                value={getBtnName('REQUEST_VENUE_REGISTRATION')}
                handleClick={handleVenueRequest}
                StyledBtn={VenueRequestButton}
              />
            </EmptyAction>
          </EmptyState>
        )}
        {!isSearching && results.map((venue) => (
          <SearchResult key={venue.id} type="button" onClick={() => handleSelect(venue)}>
            <ResultName>{venue.name.trim() || venue.nameEn.trim() || venue.venueCode}</ResultName>
            <ResultLocation>{[venue.city, venue.district].filter(Boolean).join(' ')}</ResultLocation>
          </SearchResult>
        ))}
      </SearchResults>
    </Container>
  );
};

export default ProjectInfoVenueSearchModal;

const EmptyState = styled.div`
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const EmptyAction = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  white-space: nowrap;
`;

const VenueRequestButton = styled.button`
  padding: 0;
  color: inherit;
  background: transparent;
  border: none;
  font: inherit;
  font-style: italic;
  text-decoration: underline;
  cursor: pointer;
  transition: all 0.3s ease-in-out;

  &:hover {
    transform: scale(1.05);
    color: ${({ theme }) => theme.colors.color_Gray_03};
  }
`;
